import React, { useState } from 'react';
import { Select, Switch, Popover } from 'antd';
import { Trans } from 'react-i18next';
import classNames from 'classnames';
import { DateTime } from '@whatsper/texterchat-common';
import styles from './WorkingHoursGrid.module.scss';
const { Option } = Select;
const DAYS = [
    DateTime.WEEKDAY.sunday,
    DateTime.WEEKDAY.monday,
    DateTime.WEEKDAY.tuesday,
    DateTime.WEEKDAY.wednesday,
    DateTime.WEEKDAY.thursday,
    DateTime.WEEKDAY.friday,
    DateTime.WEEKDAY.saturday,
];
const DAY_LABELS = {
    [DateTime.WEEKDAY.sunday]: 'sun',
    [DateTime.WEEKDAY.monday]: 'mon',
    [DateTime.WEEKDAY.tuesday]: 'tue',
    [DateTime.WEEKDAY.wednesday]: 'wed',
    [DateTime.WEEKDAY.thursday]: 'thu',
    [DateTime.WEEKDAY.friday]: 'fri',
    [DateTime.WEEKDAY.saturday]: 'sat',
};
const timeStringToSeconds = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 3600 + minutes * 60;
};
const secondsToTimeString = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};
const generateTimeOptions = () => {
    const options = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 30) {
            const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
            options.push({ value: time, label: time });
        }
    }
    return options;
};
const TIME_OPTIONS = generateTimeOptions();
const DEFAULT_BEGIN = timeStringToSeconds('09:00');
const DEFAULT_END = timeStringToSeconds('18:00');
export const getDefaultWorkingHoursByRegion = (region) => {
    const openDay = [{ begin: DEFAULT_BEGIN, end: DEFAULT_END }];
    const closedDay = [];
    const effectiveRegion = region || 'IL';
    if (effectiveRegion === 'IL') {
        return {
            [DateTime.WEEKDAY.sunday]: openDay,
            [DateTime.WEEKDAY.monday]: openDay,
            [DateTime.WEEKDAY.tuesday]: openDay,
            [DateTime.WEEKDAY.wednesday]: openDay,
            [DateTime.WEEKDAY.thursday]: openDay,
            [DateTime.WEEKDAY.friday]: closedDay,
            [DateTime.WEEKDAY.saturday]: closedDay,
        };
    }
    else {
        return {
            [DateTime.WEEKDAY.sunday]: closedDay,
            [DateTime.WEEKDAY.monday]: openDay,
            [DateTime.WEEKDAY.tuesday]: openDay,
            [DateTime.WEEKDAY.wednesday]: openDay,
            [DateTime.WEEKDAY.thursday]: openDay,
            [DateTime.WEEKDAY.friday]: openDay,
            [DateTime.WEEKDAY.saturday]: closedDay,
        };
    }
};
export const defaultWorkingHours = getDefaultWorkingHoursByRegion('IL');
const isDayClosed = (dayTimeframes) => {
    return !dayTimeframes || dayTimeframes.length === 0;
};
const getDayTimeframe = (dayTimeframes) => {
    if (!dayTimeframes || dayTimeframes.length === 0) {
        return { from: '09:00', to: '18:00' };
    }
    const first = dayTimeframes[0];
    return {
        from: secondsToTimeString(first.begin),
        to: secondsToTimeString(first.end),
    };
};
const WorkingHoursGrid = ({ value, onChange, disabled }) => {
    const [openPopover, setOpenPopover] = useState(null);
    const handleTimeChange = (day, field, timeString) => {
        const currentTimeframe = getDayTimeframe(value[day]);
        const newTimeframe = {
            begin: field === 'from' ? timeStringToSeconds(timeString) : timeStringToSeconds(currentTimeframe.from),
            end: field === 'to' ? timeStringToSeconds(timeString) : timeStringToSeconds(currentTimeframe.to),
        };
        onChange(Object.assign(Object.assign({}, value), { [day]: [newTimeframe] }));
    };
    const handleClosedToggle = (day, closed) => {
        if (closed) {
            onChange(Object.assign(Object.assign({}, value), { [day]: [] }));
        }
        else {
            onChange(Object.assign(Object.assign({}, value), { [day]: [{ begin: DEFAULT_BEGIN, end: DEFAULT_END }] }));
        }
    };
    const renderPopoverContent = (day) => {
        const dayTimeframes = value[day];
        const isClosed = isDayClosed(dayTimeframes);
        const { from, to } = getDayTimeframe(dayTimeframes);
        return (React.createElement("div", { className: styles.popoverContent },
            React.createElement("div", { className: styles.popoverTitle },
                React.createElement(Trans, { i18nKey: "setWorkingTime", defaults: "Set working time:" })),
            React.createElement("div", { className: styles.timeSelectors },
                React.createElement("div", { className: styles.timeSelector },
                    React.createElement("span", { className: styles.timeLabel },
                        React.createElement(Trans, { i18nKey: "from", defaults: "From" })),
                    React.createElement(Select, { value: from, onChange: (val) => handleTimeChange(day, 'from', val), disabled: isClosed || disabled, className: styles.timeSelect, size: "small", dropdownMatchSelectWidth: false }, TIME_OPTIONS.map((opt) => (React.createElement(Option, { key: opt.value, value: opt.value }, opt.label))))),
                React.createElement("span", { className: styles.timeSeparator }, "-"),
                React.createElement("div", { className: styles.timeSelector },
                    React.createElement("span", { className: styles.timeLabel },
                        React.createElement(Trans, { i18nKey: "to", defaults: "To" })),
                    React.createElement(Select, { value: to, onChange: (val) => handleTimeChange(day, 'to', val), disabled: isClosed || disabled, className: styles.timeSelect, size: "small", dropdownMatchSelectWidth: false }, TIME_OPTIONS.map((opt) => (React.createElement(Option, { key: opt.value, value: opt.value }, opt.label)))))),
            React.createElement("div", { className: styles.closedToggle },
                React.createElement(Switch, { checked: isClosed, onChange: (checked) => handleClosedToggle(day, checked), disabled: disabled, size: "small", className: styles.closedSwitch }),
                React.createElement("span", { className: styles.closedLabel },
                    React.createElement(Trans, { i18nKey: "allDayClosed", defaults: "All day closed" })))));
    };
    const renderDayCell = (day) => {
        const dayTimeframes = value[day];
        const isClosed = isDayClosed(dayTimeframes);
        const { from, to } = getDayTimeframe(dayTimeframes);
        return (React.createElement(Popover, { key: day, content: renderPopoverContent(day), trigger: "click", open: openPopover === day, onOpenChange: (open) => setOpenPopover(open ? day : null), placement: "bottom" },
            React.createElement("div", { className: classNames(styles.dayCell, {
                    [styles.closed]: isClosed,
                    [styles.disabled]: disabled,
                }) },
                isClosed ? (React.createElement("div", { className: styles.closedText },
                    React.createElement(Trans, { i18nKey: "closed", defaults: "Closed" }))) : (React.createElement("div", { className: styles.hoursDisplay },
                    React.createElement("div", { className: styles.hourRow },
                        React.createElement("span", { className: styles.hourLabel },
                            React.createElement(Trans, { i18nKey: "from", defaults: "From" })),
                        React.createElement("span", { className: styles.hourValue }, from)),
                    React.createElement("div", { className: styles.hourRow },
                        React.createElement("span", { className: styles.hourLabel },
                            React.createElement(Trans, { i18nKey: "to", defaults: "To" })),
                        React.createElement("span", { className: styles.hourValue }, to)))),
                React.createElement("div", { className: styles.editIcon },
                    React.createElement("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                        React.createElement("path", { d: "M8.5 1.5L10.5 3.5L4 10H2V8L8.5 1.5Z", stroke: "currentColor", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }))))));
    };
    return (React.createElement("div", { className: classNames(styles.workingHoursGrid, { [styles.disabled]: disabled }) },
        React.createElement("div", { className: styles.headerRow }, DAYS.map((day) => (React.createElement("div", { key: day, className: styles.dayHeader },
            React.createElement(Trans, { i18nKey: DAY_LABELS[day] }))))),
        React.createElement("div", { className: styles.contentRow }, DAYS.map((day) => renderDayCell(day)))));
};
export default WorkingHoursGrid;
